import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Socket } from "socket.io-client";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useSeedDeepResearchRuns } from "@/pages/Private/redux/deepResearch/useSeedDeepResearchRuns";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";

import {
	DeepResearchResultController,
	DeepResearchResultUpdate,
	useDeepResearchResultController,
} from "./useDeepResearchResultController";

enum DeepResearchSocketAction {
	JOIN_COMPANY = "deep-research-join-company",
	DEEP_RESEARCH_RESULT = "deep-research-result",
	ERROR = "deep-research-error",
}

export type DeepResearchSocketContextType = Omit<
	DeepResearchResultController,
	"applyDeepResearchResult"
> & { isConnected: boolean };

const noopBoolean = () => false;
const noopVoid = () => {};
const noopNull = () => null;

/** The context value of a controller: everything but the result handler, which the transport owns. */
export const deepResearchContextValue = (
	controller: DeepResearchResultController,
	isConnected: boolean
): DeepResearchSocketContextType => ({
	isConnected,
	isPending: controller.isPending,
	getRun: controller.getRun,
	startPending: controller.startPending,
	settlePending: controller.settlePending,
	getFailure: controller.getFailure,
	clearError: controller.clearError,
	seedRuns: controller.seedRuns,
});

/**
 * Exported so alternative transports (the Chrome-extension background bridge)
 * can supply their own value while the panel keeps reading it through
 * `useDeepResearchSocketContext` / `useDeepResearchSocket`.
 */
export const DeepResearchSocketContext = createContext<DeepResearchSocketContextType>({
	isConnected: false,
	isPending: noopBoolean,
	getRun: noopNull,
	startPending: noopVoid,
	settlePending: noopVoid,
	getFailure: noopNull,
	clearError: noopVoid,
	seedRuns: noopVoid,
});

const MAX_SEEDED_LEADS = 100;

/**
 * The prospects of the leads the page has loaded into the list slice (the list table, the
 * campaign overview and the Messages surfaces), so each shows its research's real state
 * (PREC-4052). At most one page: the endpoint takes 100.
 */
export const loadedLeadProspectIds = (
	inbox: Array<{ prospect?: { id?: number | null } | null }> | null | undefined
): number[] => {
	const ids = (inbox ?? []).map((lead) => lead.prospect?.id).filter((id): id is number => !!id);

	return [...new Set(ids)].slice(0, MAX_SEEDED_LEADS);
};

const useLoadedLeadProspectIds = (): number[] =>
	loadedLeadProspectIds(useAppSelector(listSelector).inbox);

export const DeepResearchSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const socketRef = useRef<Socket | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const controller = useDeepResearchResultController();
	const { applyDeepResearchResult } = controller;
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const tokenRef = useRef(token);

	tokenRef.current = token;
	const companyId = auth.user?.company?.id;

	useEffect(() => {
		if (!token || !companyId) {
			return;
		}

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		let hasLoggedError = false;

		newSocket.on("connect", () => {
			newSocket.emit(DeepResearchSocketAction.JOIN_COMPANY, String(companyId));
			hasLoggedError = false;
			setIsConnected(true);
		});

		newSocket.on("connect_error", (err) => {
			if (!hasLoggedError) {
				console.warn("Deep research socket connection error:", err);
				hasLoggedError = true;
			}

			setIsConnected(false);
		});

		newSocket.on("disconnect", () => {
			setIsConnected(false);
		});

		newSocket.on(DeepResearchSocketAction.DEEP_RESEARCH_RESULT, (data: DeepResearchResultUpdate) =>
			applyDeepResearchResult(data)
		);

		newSocket.on(DeepResearchSocketAction.ERROR, (errorMessage: string) => {
			console.error("Deep research socket error:", errorMessage);
		});

		socketRef.current = newSocket;

		return () => {
			newSocket.disconnect();
			socketRef.current = null;
		};
	}, [token, companyId, applyDeepResearchResult]);

	useSeedDeepResearchRuns(useLoadedLeadProspectIds(), controller);

	return (
		<DeepResearchSocketContext.Provider value={deepResearchContextValue(controller, isConnected)}>
			{children}
		</DeepResearchSocketContext.Provider>
	);
};

export const useDeepResearchSocketContext = () => {
	return useContext(DeepResearchSocketContext);
};
